"use client";

import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useIsLens } from "@/lib/audienceLens/AudienceLensContext";

/**
 * M3 — Regulator lens evidence panel. Surfaces the compliance evidence
 * chain a regulator wants to see: sanctions-list cadence + provenance,
 * Travel Rule payload details, audit retention + anchor hashes, data-
 * residency assertions. Renders ONLY on `?lens=regulator`.
 *
 * Variants:
 *
 *   variant="compliance-snapshot" — anchored to ComplianceSnapshot
 *                                    (2.6 / 4.9 / 5.11). Shows the full
 *                                    audit chain backing the snapshot
 *                                    + retention assertions.
 */
export function RegulatorEvidence({
  variant,
  corridorId,
  snapshotId,
  lcReference,
  className,
}: {
  variant: "compliance-snapshot" | "lc-issued" | "settlement";
  corridorId?: string;
  snapshotId?: string;
  lcReference?: string;
  className?: string;
}) {
  const t = useTranslations("regulatorEvidence");
  const isRegulator = useIsLens("regulator");
  if (!isRegulator) return null;
  const dash = t("dashPlaceholder");

  if (variant === "compliance-snapshot") {
    return (
      <div className={clsx("glass p-4", className)}>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
            {t("kicker")}
          </p>
          <span className="rounded-full border border-state-traced/30 bg-state-traced/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-traced">
            {t("lensChip")}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("anchorChainTitle", { corridor: corridorId ?? dash })}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("snapshotIdLabel")} <span className="font-mono text-state-traced">{snapshotId ?? dash}</span></li>
              <li>{t("hcsTopicLabel")} <span className="font-mono text-state-traced">0.0.4801250</span></li>
              <li>{t("anchorSeqLabel")} <span className="font-mono text-state-traced">7</span></li>
              <li>{t("contentHashLabel")} <span className="font-mono text-state-traced">sha256:b21d…4e83</span></li>
              <li>{t("witnessCountLabel")} <span className="font-mono">{t("witnessValueCorridor")}</span></li>
              <li>{t("anchoredLabel")} <span className="font-mono">{t("anchoredValueSnapshot")}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("retentionTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("retentionLabel")} <span className="font-mono text-state-traced">{t("retentionValue")}</span> {t("retentionSuffix")}</li>
              <li>{t("immutabilityLabel")} <span className="font-mono">{t("immutabilityValue")}</span></li>
              <li>{t("replayLabel")} <span className="font-mono">{t("replayValue")}</span></li>
              <li>{t("travelRuleLabel")} <span className="font-mono">{t("travelRuleValue")}</span></li>
              <li>{t("rightErasureLabel")} <span className="font-mono">{t("rightErasureValue")}</span></li>
              <li>{t("crossBorderLabel")} <span className="font-mono">{t("crossBorderValue")}</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-3 rounded border border-state-traced/30 bg-state-traced/8 px-3 py-2 text-[10px] text-state-traced">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em]">
            {t("screeningTitle")}
          </p>
          <ul className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
            <li className="text-orbitra-paper">{t("screeningOfac")}</li>
            <li className="text-orbitra-paper">{t("screeningEu")}</li>
            <li className="text-orbitra-paper">{t("screeningUn")}</li>
            <li className="text-orbitra-paper">{t("screeningUk")}</li>
            <li className="text-orbitra-paper">{t("screeningLocal")}</li>
            <li className="text-orbitra-paper">{t("screeningAdverse")}</li>
          </ul>
        </div>

        <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
          {t("footerPrefix")}
          <span className="ms-1 font-mono text-state-traced">{t("footerToggle")}</span>{t("footerToggleSuffix")}
        </p>
      </div>
    );
  }

  if (variant === "lc-issued") {
    return (
      <div className={clsx("glass p-4", className)}>
        <RegulatorHeader />
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("lcAuditTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("lcReferenceLabel")} <span className="font-mono text-state-traced">{lcReference ?? dash}</span></li>
              <li>{t("lcIssuanceLabel")} <span className="font-mono">{t("lcIssuanceValue")}</span></li>
              <li>{t("anchorSeqLabel")} <span className="font-mono text-state-traced">{t("lcAnchorSeqValue")}</span></li>
              <li>{t("contentHashLabel")} <span className="font-mono text-state-traced">{t("lcContentHashValue")}</span></li>
              <li>{t("witnessCountLabel")} <span className="font-mono">{t("lcWitnessValue")}</span></li>
              <li>{t("anchoredLabel")} <span className="font-mono">{t("lcAnchoredValue")}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("lcComplianceTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("lcUboLabel")} <span className="font-mono text-state-traced">{t("lcUboValue")}</span></li>
              <li>{t("lcSanctionsLabel")} <span className="font-mono">{t("lcSanctionsValue")}</span></li>
              <li>{t("lcTravelRuleLabel")} <span className="font-mono">{t("lcTravelRuleValue")}</span></li>
              <li>{t("lcSuspiciousLabel")} <span className="font-mono">{t("lcSuspiciousValue")}</span></li>
              <li>{t("lcErasureLabel")} <span className="font-mono">{t("lcErasureValue")}</span></li>
            </ul>
          </div>
        </div>
        <RegulatorFooter />
      </div>
    );
  }

  if (variant === "settlement") {
    return (
      <div className={clsx("glass p-4", className)}>
        <RegulatorHeader />
        <div className="mt-3 grid grid-cols-2 gap-4 text-[11px]">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("settlementFinalityTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("settlementTriggerLabel")} <span className="font-mono text-state-traced">{t("settlementTriggerValue")}</span></li>
              <li>{t("settlementAnchorLabel")} <span className="font-mono">{t("settlementAnchorValue")}</span></li>
              <li>{t("settlementFinalityLabel")} <span className="font-mono text-state-traced">{t("settlementFinalityValue")}</span></li>
              <li>{t("settlementReversibilityLabel")} <span className="font-mono">{t("settlementReversibilityValue")}</span></li>
              <li>{t("settlementDisputeLabel")} <span className="font-mono">{t("settlementDisputeValue")}</span></li>
            </ul>
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("governanceTitle")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-orbitra-paper">
              <li>{t("governanceLegsLabel")} <span className="font-mono text-state-traced">{t("governanceLegsValue")}</span></li>
              <li>{t("governanceResidencyLabel")} <span className="font-mono">{t("governanceResidencyValue")}</span></li>
              <li>{t("governanceAuditLabel")} <span className="font-mono">{t("governanceAuditValue")}</span></li>
              <li>{t("governanceTaxLabel")} <span className="font-mono">{t("governanceTaxValue")}</span></li>
              <li>{t("governanceReplayLabel")} <span className="font-mono text-state-traced">{t("governanceReplayValue")}</span></li>
            </ul>
          </div>
        </div>
        <RegulatorFooter />
      </div>
    );
  }

  return null;
}

function RegulatorHeader() {
  const t = useTranslations("regulatorEvidence");
  return (
    <div className="flex items-baseline justify-between">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
        {t("kicker")}
      </p>
      <span className="rounded-full border border-state-traced/30 bg-state-traced/8 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-state-traced">
        {t("lensChip")}
      </span>
    </div>
  );
}

function RegulatorFooter() {
  const t = useTranslations("regulatorEvidence");
  return (
    <p className="mt-3 font-mono text-[9px] text-orbitra-muted">
      {t("footerPrefix")}
      <span className="ms-1 font-mono text-state-traced">{t("footerToggle")}</span>{t("footerToggleSuffix")}
    </p>
  );
}
